import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { environment } from "@environments/environment";
import {
  Ticket,
  TicketAttachment,
  TicketCategory,
  TicketStatus,
  GetTicketsRequest,
  CreateTicketRequest,
  UpdateTicketRequest,
  GetTicketsResponse,
  TicketComment,
} from "@models/ticket-data.model";

@Injectable({
  providedIn: "root",
})
export class IssueTrackerService {
  private readonly baseUrl = `${environment.apiUrl}/Support`;

  constructor(private http: HttpClient) {}

  getTickets(request: GetTicketsRequest): Observable<GetTicketsResponse> {
    let params = new HttpParams()
      .set("organizationId", request.organizationId.toString())
      .set("organizationEMRId", request.organizationEMRId.toString());

    return this.http.get<GetTicketsResponse>(`${this.baseUrl}/tickets`, {
      params,
    });
  }

  getTicketById(ticketId: number): Observable<Ticket> {
    return this.http.get<Ticket>(`${this.baseUrl}/tickets/${ticketId}`);
  }

  createTicket(ticket: CreateTicketRequest): Observable<Ticket> {
    return this.http.post<Ticket>(`${this.baseUrl}/tickets`, ticket);
  }

  updateTicket(
    ticketId: number,
    updates: UpdateTicketRequest
  ): Observable<Ticket> {
    return this.http.put<Ticket>(
      `${this.baseUrl}/tickets/${ticketId}`,
      updates
    );
  }

  deleteTicket(ticketId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/tickets/${ticketId}`);
  }

  getTicketCategories(): Observable<TicketCategory[]> {
    return this.http.get<TicketCategory[]>(
      `${this.baseUrl}/tickets/categories`
    );
  }

  getTicketStatuses(): Observable<TicketStatus[]> {
    return this.http.get<TicketStatus[]>(`${this.baseUrl}/tickets/statuses`);
  }

  uploadTicketAttachment(
    ticketId: number,
    file: File,
    isPhi: boolean,
    dataEnrichmentDocumentType: string,
    dataEnrichmentDocumentDate: string
  ): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("isPhi", isPhi.toString());
    formData.append("documentType", dataEnrichmentDocumentType);
    formData.append("documentDate", dataEnrichmentDocumentDate);

    return this.http.post(
      `${this.baseUrl}/tickets/${ticketId}/attachments`,
      formData
    );
  }

  updateTicketAttachment(
    attachmentId: number,
    documentType: string,
    documentDate: string
  ): Observable<TicketAttachment> {
    return this.http.put<TicketAttachment>(
      `${this.baseUrl}/tickets/attachments/${attachmentId}`,
      { documentType, documentDate }
    );
  }

  deleteTicketAttachment(attachmentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/tickets/attachments/${attachmentId}`
    );
  }

  addTicketComment(
    ticketId: number,
    comment: string
  ): Observable<TicketComment> {
    const request = { comment };

    return this.http.post<TicketComment>(
      `${this.baseUrl}/tickets/${ticketId}/comments`,
      request
    );
  }

  updateTicketComment(
    commentId: number,
    comment: string
  ): Observable<TicketComment> {
    const request = { comment };

    return this.http.put<TicketComment>(
      `${this.baseUrl}/tickets/comments/${commentId}`,
      request
    );
  }

  deleteTicketComment(commentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/tickets/comments/${commentId}`
    );
  }

  downloadAttachment(
    attachmentId: number
  ): Observable<{ blob: Blob; fileName: string }> {
    const params = new HttpParams().set("asAttachment", "true");
    return this.http
      .get(`${this.baseUrl}/tickets/attachments/${attachmentId}/download`, {
        params,
        responseType: "blob",
        observe: "response",
      })
      .pipe(
        map((response) => {
          const contentDisposition = response.headers.get(
            "Content-Disposition"
          );
          let fileName = `attachment_${attachmentId}`;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              fileName = match[1];
            }
          }
          return { blob: response.body as Blob, fileName };
        })
      );
  }

  previewAttachment(
    attachmentId: number
  ): Observable<{ blob: Blob; fileName: string }> {
    const params = new HttpParams().set("asAttachment", "false");
    return this.http
      .get(`${this.baseUrl}/tickets/attachments/${attachmentId}/download`, {
        params,
        responseType: "blob",
        observe: "response",
      })
      .pipe(
        map((response) => {
          const contentDisposition = response.headers.get(
            "Content-Disposition"
          );
          let fileName = `attachment_${attachmentId}`;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              fileName = match[1];
            }
          }
          return { blob: response.body as Blob, fileName };
        })
      );
  }
}
